import { BadgeCheck, CloudCheck, LogOut } from 'lucide-react'
import { NavLink, useLocation } from 'react-router'

import { getAcronymFromString } from '@fortem-monorepo/js-utilities'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenuButton,
  useSidebar,
} from '@fortem-monorepo/react-ui'

import { CanAccess } from '@/components/CanAccess'
import { ACCOUNT } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import { useLogout } from '@/hooks/use-logout'
import { useUserContext } from '@/index'
import { useWhitelistListsailMutation } from '@/react-query/mutations/account'

export interface GlobalSidebarUserProps {}

export const GlobalSidebarUser: React.FC<GlobalSidebarUserProps> = () => {
  const { isMobile } = useSidebar()
  const {
    user: { user },
  } = useUserContext()
  const logout = useLogout()
  const { pathname } = useLocation()

  const { withConfirmation, Dialog: WhitelistDialog } = useConfirmModal({
    title: "Confirm you're not in public",
    description:
      "You must not use this function if you\'re using a personal hotspot or in a public area.",
  })

  const whitelistIPMutation = useWhitelistListsailMutation()

  const handleWhitelist = async () =>
    withConfirmation(async () => whitelistIPMutation.mutateAsync())

  return (
    <>
      <WhitelistDialog />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton>
            <Avatar className="">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="rounded-lg">
                {getAcronymFromString(user?.name)}
              </AvatarFallback>
            </Avatar>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
          side={isMobile ? 'bottom' : 'right'}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="rounded-lg">CN</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{user?.name}</span>
                <span className="truncate text-xs">{user?.email}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild disabled={pathname.endsWith(ACCOUNT.DASHBOARD.link)}>
              <NavLink to={ACCOUNT.DASHBOARD.link}>
                <BadgeCheck />
                Account
              </NavLink>
            </DropdownMenuItem>
            <CanAccess permissions={[PERMISSIONS.ACCESS_V1]}>
              <DropdownMenuItem onClick={() => handleWhitelist()}>
                <CloudCheck />
                Whitelist V1
              </DropdownMenuItem>
            </CanAccess>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout}>
            <LogOut />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
